import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { MlyGrid } from './shared/MlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMonthlyData } from '../../hooks/useMonthlyData'
import { useMonthlyNormals } from '../../hooks/useMonthlyNormals'

const SLUG = 'mly-prehled'

const ROW_GROUPS = [
  {
    label: 'Teplota vzduchu',
    rows: [
      { tsId: 'TEMPAVG_AVG',    label: 'Průměrná teplota',        unit: '°C',   normalMode: 'diff', decimals: 1 },
      { tsId: 'TXMAX_MAX',      label: 'Max. denních maxim',       unit: '°C',   normalMode: 'diff', decimals: 1 },
      { tsId: 'TNMIN_MIN',      label: 'Min. denních minim',       unit: '°C',   normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Povrchová teplota',
    rows: [
      { tsId: 'TGROUNDMIN_MIN', label: 'Min. povrchová teplota',   unit: '°C',   normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Srážky & sníh',
    rows: [
      { tsId: 'PRECIPSUM_SUM',  label: 'Srážkový úhrn',            unit: 'mm',   normalMode: 'pct',  decimals: 1 },
      { tsId: 'PRECIPSUM_MAX',  label: 'Denní maximum srážek',      unit: 'mm',   normalMode: null,   decimals: 1 },
      { tsId: 'SNOW_MAX',       label: 'Maximum sněhové pokrývky', unit: 'cm',   normalMode: null,   decimals: 0 },
    ],
  },
  {
    label: 'Sluneční svit & záření & oblačnost',
    rows: [
      { tsId: 'SUNSHINESUM_SUM',  label: 'Sluneční svit',          unit: 'hod',    normalMode: 'pct',  decimals: 1 },
      { tsId: 'GLOBALRADSUM_SUM', label: 'Globální záření',         unit: 'kJ/m²', normalMode: 'pct',  decimals: 0 },
      { tsId: 'CLOUDAVG_AVG',     label: 'Průměrná oblačnost',      unit: 'okty',  normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Vítr',
    rows: [
      { tsId: 'WSAVG_AVG',  label: 'Průměrná rychlost větru', unit: 'm/s', normalMode: 'diff', decimals: 1 },
      { tsId: 'WSMAX_MAX',  label: 'Maximum nárazu větru',     unit: 'm/s', normalMode: 'diff', decimals: 1 },
    ],
  },
  {
    label: 'Tlak & vlhkost',
    rows: [
      { tsId: 'PRESSAVG_AVG',     label: 'Průměrný tlak',           unit: 'hPa', normalMode: 'diff', decimals: 1 },
      { tsId: 'HUMIDITYAVG_AVG',  label: 'Průměrná rel. vlhkost',   unit: '%',   normalMode: 'diff', decimals: 1 },
      { tsId: 'VAPPRESSAVG_AVG',  label: 'Tlak vodních par',         unit: 'hPa', normalMode: 'diff', decimals: 1 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka (kratší popisy veličin).
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  TEMPAVG_AVG: 'Prům. teplota', TXMAX_MAX: 'Max. denních max.', TNMIN_MIN: 'Min. denních min.',
  TGROUNDMIN_MIN: 'Min. povrchová',
  PRECIPSUM_SUM: 'Úhrn', PRECIPSUM_MAX: 'Denní max.', SNOW_MAX: 'Max. sníh',
  SUNSHINESUM_SUM: 'Svit', GLOBALRADSUM_SUM: 'Glob. záření', CLOUDAVG_AVG: 'Oblačnost',
  WSAVG_AVG: 'Prům. rychlost', WSMAX_MAX: 'Max. náraz',
  PRESSAVG_AVG: 'Tlak', HUMIDITYAVG_AVG: 'Rel. vlhkost', VAPPRESSAVG_AVG: 'Tlak vod. par',
}, {
  'Sluneční svit & záření & oblačnost': 'Svit & záření',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function MlyPrehledView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, yearMap, dtMap, yearDtMap, isLoading: dLoading, isError: dError } = useMonthlyData(station?.gh_id, year, TS_IDS)
  const { normalsMap, yearNormalsMap, isLoading: nLoading }        = useMonthlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <MlyGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            yearMap={yearMap}
            dtMap={dtMap}
            yearDtMap={yearDtMap}
            normalsMap={normalsMap}
            yearNormalsMap={yearNormalsMap}
          />
        )}
      </div>
    </div>
  )
}
